import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAdminOrganizationMembersQuery } from '../../api/admin';
import AdminGridLink from './AdminGridLink';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	organizationId: number;
	/** When set, only members of one of these teams are listed (team names are unique within an organization). */
	teamNames?: string[];
	height?: string | number;
}

const columns: GridColDef[] = [
	{
		field: 'full_name',
		headerName: 'Name',
		flex: 1,
		minWidth: 160,
		renderCell: (params: GridRenderCellParams) => (
			<AdminGridLink
				to={`/admin/users/${params.row.user_id}`}
				state={{ userEmail: params.row.email, userName: params.row.full_name }}
			>
				{params.value}
			</AdminGridLink>
		),
	},
	{ field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
	{
		field: 'team_names',
		headerName: 'Teams',
		flex: 1.5,
		minWidth: 200,
		sortable: false,
		// join for quick-filter / CSV export; chips for display
		valueGetter: ({ row }) => (row.team_names ?? []).join(', '),
		renderCell: (params: GridRenderCellParams) => {
			const names: string[] = params.row.team_names ?? [];
			if (names.length === 0) {
				return null;
			}
			return (
				<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
					{names.map((name) => (
						<Chip key={name} label={name} size="small" variant="outlined" />
					))}
				</Stack>
			);
		},
	},
	{ field: 'is_active', headerName: 'Active', width: 90, type: 'boolean' },
];

export default function OrganizationMembersTable({ organizationId, teamNames, height = 'calc(100vh - 560px)' }: Props) {
	const { data: members, isLoading } = useAdminOrganizationMembersQuery(organizationId);
	const rows = teamNames
		? (members ?? []).filter((m) => m.team_names.some((name) => teamNames.includes(name)))
		: members ?? [];

	return (
		<Box sx={{ height, minHeight: 300, width: '100%' }}>
			<DataGrid
				rows={rows}
				getRowId={(row) => row.user_id}
				columns={columns}
				loading={isLoading}
				density="compact"
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>
		</Box>
	);
}
